/**
 * Single source of name-led Workshop chrome (spec §4.8). Every rendered
 * title/placeholder comes from here so no component hardcodes a gendered
 * pronoun — copy adapts to any persona type by name, never mis-genders.
 */
export interface WorkshopCopy {
  whoTitle: string
  howTitle: string
  canDoTitle: string
  rulesTitle: string
  knowTitle: string
  extraTitle: string
  advancedTitle: string
  coachPlaceholder: string
  coachTitle: string
  coachIntro: string
  coachUnavailable: string
  coachStreamError: string
}

export function workshopCopy(name: string): WorkshopCopy {
  const who = name.trim() || 'this persona'
  return {
    whoTitle: `Who ${who} is`,
    howTitle: `How ${who} talks & behaves`,
    canDoTitle: `What ${who} can do`,
    rulesTitle: 'Rules & guardrails',
    knowTitle: 'Knowledge & examples',
    extraTitle: 'Formatting & extras',
    advancedTitle: 'Advanced',
    coachPlaceholder: `Coach ${who} in plain language…`,
    coachTitle: `Coach ${who}`,
    coachIntro: `Give ${who} feedback in plain language — ${who} can suggest changes and you decide what to keep.`,
    coachUnavailable: `Coaching needs an OpenAI API key. Add one under Settings → API Keys, or ask an admin to assign you a credential set.`,
    coachStreamError: `The coach hit an error mid-reply — nothing was changed. Try again.`,
  }
}

/**
 * Runtime-layer strip copy. Not part of WorkshopCopy — these strings are
 * persona-name-independent. The backend returns layer STATE only; the
 * prose lives here with the rest of the Workshop's chrome copy.
 */
export const RUNTIME_LAYER_COPY: Record<string, { label: string; description: string }> = {
  skill: { label: 'Skills', description: 'Instructions for any skill that matches the customer’s message.' },
  workflow_context: { label: 'Workflow context', description: 'Where the customer has got to in an order or booking.' },
  tool_manifest: { label: 'Available tools', description: 'The tools this persona is allowed to call on a turn.' },
  customer_context: { label: 'Customer profile', description: 'What’s already known about the person in the conversation.' },
  situational: { label: 'Situational', description: 'Signals about the current turn, such as the detected language.' },
}

export const RUNTIME_STRIP_INTRO =
  'These layers are added automatically during a real conversation. They aren’t editable here, and most only appear once a conversation is actually under way.'

/**
 * "Rendered for" conversation picker (inspector header) — sentinel option
 * label for the saved-bundle-only render, and the honesty label shown when
 * a real conversation is selected instead.
 */
export const RENDERED_FOR_BUNDLE_ONLY_LABEL = 'Saved bundle only'

/**
 * The reconstruction honesty label, split into plain-string segments so
 * `ComposedPromptInspector.tsx` can wrap "current" and "not" in `<strong>` —
 * those two words ARE the safety point (this is the conversation's CURRENT
 * state; workflow-turn context is NOT included), so they need visual
 * emphasis, not just prose. This file stays `.ts` (no JSX), so the
 * emphasis is composed at the call site from these segments; concatenating
 * every field in order reproduces the exact original sentence
 * character-for-character:
 *
 *   "Reconstruction: runtime layers rendered against this conversation's
 *   current state. Workflow-turn context (guardrails, facts, agenda) is
 *   engine-assembled per turn and is not included — see turn capture."
 */
export const RECONSTRUCTION_HONESTY_LABEL = {
  beforeCurrent: "Reconstruction: runtime layers rendered against this conversation's ",
  current: 'current',
  betweenCurrentAndNot:
    ' state. Workflow-turn context (guardrails, facts, agenda) is engine-assembled per turn and is ',
  not: 'not',
  after: ' included — see turn capture.',
}

/**
 * "System layers" tab intro (inspector, Task 9) — the engine-side
 * extraction/render/post guardrails from `GET /api/prompt-inventory`.
 * Same disclosure-not-editing framing as `RUNTIME_STRIP_INTRO`.
 */
export const SYSTEM_LAYERS_INTRO =
  'These rules apply to every persona on workflow/chat turns; they are not editable here.'

/**
 * Turn picker (inspector, Task 16) — sentinel option label for "no real
 * snapshot selected, fall back to the Task 8 reconstruction", and the
 * badge shown once the user picks an actual captured turn instead.
 * Unlike `RECONSTRUCTION_HONESTY_LABEL`, this is a plain string — there's
 * no inline emphasis to compose, since "captured" is unambiguous on its
 * own (no current-vs-not distinction to call out).
 */
export const CURRENT_STATE_RECONSTRUCTION_LABEL = 'Current state (reconstruction)'

export const CAPTURED_LABEL_TEXT =
  'Captured at render time — this is the exact prompt this turn received.'

/**
 * Snapshot-detail failure copy (reviewer fix wave, Task 16) — the detail
 * query uses `retry: false` (same as every other inspector read), so an
 * error here is permanent for this render; the turn picker still shows
 * the snapshot as selected; this replaces the content area instead of
 * silently falling back to the (misleading, since a real turn WAS picked)
 * reconstruction.
 */
export const SNAPSHOT_LOAD_ERROR_TEXT = "Couldn't load this snapshot."

/**
 * Snapshot-mode footer (reviewer fix wave, Task 16) — replaces the
 * reconstruction's `≈ N tokens total` line, which is drawn from the
 * BREAKDOWN query and would either lie (stale bundle numbers under an
 * unrelated captured turn) or crash (breakdown is null when the current
 * bundle can't recompose — the headline reason to reach for a capture in
 * the first place). Derived straight from the text/sections actually on
 * screen, so it can never drift from what's displayed.
 */
export function snapshotFooterText(systemText: string, sectionCount: number): string {
  return `${systemText.length} chars · ${sectionCount} sections`
}
